import type { MouseEvent } from "react";
import { hillEndText, type HillHud as HillHudState } from "../game/hill";
import { HILL_WEIGHTS, weighted } from "../game/hill-score";
import { HILL_CLOSE_BONUS } from "../game/sim/tuning";
import { RIDE_LABELS } from "../game/settings";
import { outline, SAFE_BOTTOM } from "./Hud";
import { SECONDARY } from "./buttons";
import { LABEL, LABEL_TYPE, NOTE, PANEL, tile, TITLE } from "./theme";
import { HILL_RIDES } from "../game/sim/hill";
import type { RideId } from "../game/sim/rides";
import { buttonFor, namedFor, type InputKind } from "./input-names";

// Bomb the Hill (#193): the run's distance up top, the speed against where
// the wobbles start along the bottom, and how the run ended in the middle.
// On a phone (#198) it all moves out of the road's way: the distance small
// at the top centre with the best beside it, and the speed a slim bar along
// the top edge.

const pts = (n: number) => n.toLocaleString("en-US");

/** The end card's breakdown (#205): each score, its weight and what it adds, then the total. */
function ScoreRows({ score, bestScore }: { score: NonNullable<NonNullable<HillHudState["end"]>["score"]>; bestScore: number }) {
  const rows = [
    { part: "distance", label: "Distance", value: `${score.distance} m` },
    { part: "speed", label: "Speed", value: pts(score.speed) },
    { part: "tricks", label: "Tricks", value: pts(score.tricks) },
  ] as const;
  return (
    <div aria-label="Score" className="grid grid-cols-[auto_auto_auto_auto] items-baseline gap-x-3 text-sm font-black tabular-nums">
      {rows.map((r) => (
        <div key={r.part} data-score-row={r.part} className="contents">
          <span className="uppercase">{r.label}</span>
          <span className="text-right">{r.value}</span>
          <span className="text-right text-xs opacity-70">×{HILL_WEIGHTS[r.part]}</span>
          <span className="text-right">{pts(weighted(r.part, score[r.part]))}</span>
        </div>
      ))}
      <div data-score-total className="col-span-4 flex justify-between border-t-2 border-line pt-0.5 text-base italic">
        <span className="uppercase">Total</span>
        <span>{pts(score.total)}</span>
      </div>
      <span className="col-span-4 text-center text-xs uppercase">{score.best ? "New best score!" : `Best score ${pts(bestScore)}`}</span>
    </div>
  );
}

/** How the run ended, in the middle until you're back at the top, with a way out to the lobby (#224). */
function EndCard({ hill, end, onLobby }: { hill: HillHudState; end: NonNullable<HillHudState["end"]>; onLobby?: () => void }) {
  return (
    <div role="status" className="pointer-events-none absolute inset-0 grid place-items-center">
      {/* Above the touch controls' glass, which would take the tap on Back to lobby. */}
      <div className={`${PANEL} relative z-10 flex flex-col items-center gap-1 px-6 py-3`}>
        <div className={`${TITLE} text-3xl ${end.why === "bottom" ? "text-aqua" : "text-danger"}`}>{hillEndText(end)}</div>
        <div className="text-2xl font-black italic tabular-nums">{end.distance} m down</div>
        <div className="text-sm font-black uppercase">{end.best ? "New best!" : `Best ${hill.best} m`}</div>
        {end.score && <ScoreRows score={end.score} bestScore={hill.bestScore} />}
        <div className={`${LABEL} tabular-nums`}>Road #{hill.seed}</div>
        {onLobby && (
          <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={onLobby} className={`${TAP} mt-1 px-4 text-sm`}>
            Back to lobby
          </button>
        )}
      </div>
    </div>
  );
}

/** The run's three scores while it's on (#205): speed and tricks small, the total beside them. */
function ScoreLine({ hill, compact }: { hill: HillHudState; compact: boolean }) {
  return (
    <div aria-label="Points" className={`flex items-baseline gap-2 font-black uppercase italic tabular-nums ${compact ? "text-[0.65rem]" : "text-xs sm:text-sm"} ${outline}`}>
      <span>Speed {pts(hill.speedPts)}</span>
      <span>Tricks {pts(hill.trickPts)}</span>
      <span data-score className="text-highlight">
        {pts(hill.score)} pts
      </span>
    </div>
  );
}

/** How each input steps the ride on the pick card (#207). */
const STEP_KEYS: Record<InputKind, string> = { gamepad: "◀ ▶", keyboard: "← →", touch: "Steer" };

/** The card's buttons (#203): a thumb-sized tap target that pushes in when pressed. */
const TAP = `${SECONDARY} pointer-events-auto min-h-12 touch-manipulation select-none transition-transform active:scale-90 active:bg-highlight`;

const RIDE_ICONS: Record<RideId, string> = { board: "🛹", bmx: "🚲", scooter: "🛴", skates: "🛼" };

/** What the card's buttons do (#203): step the ride one along, or drop in. */
export type PickActions = { onPick?: (dir: -1 | 1) => void; onDropIn?: () => void };

/**
 * At the top of each run (#195): which ride you'll take down, locked once you
 * drop in. Every ride is a tile (#225: no arrow buttons): a tap on another
 * steps to it, a tap on the one picked drops in (#203). It names the buttons
 * of the input you're using (#207), like the lobby's: on a pad ✕ or Start
 * drops in. The tiles don't take focus, so the keys keep going to the game.
 */
function PickCard({ ride, input, onPick, onDropIn }: { ride: NonNullable<HillHudState["picking"]>; input: InputKind } & PickActions) {
  const drop = buttonFor(input, "✕");
  const noFocus = (e: MouseEvent) => e.preventDefault();
  const at = HILL_RIDES.indexOf(ride);
  const choose = (i: number) => {
    // Steps the short way round to the tile (the sim steps one ride at a time).
    const n = HILL_RIDES.length;
    const fwd = (i - at + n) % n;
    const [dir, steps] = fwd <= n - fwd ? ([1, fwd] as const) : ([-1, n - fwd] as const);
    for (let k = 0; k < steps; k++) onPick?.(dir);
  };
  return (
    <div role="status" aria-label="Pick your ride" className="pointer-events-none absolute inset-0 grid place-items-center">
      {/* Above the touch controls' glass, which would take the taps (#203). */}
      <div className={`${PANEL} relative z-10 flex flex-col items-center gap-2 px-4 py-3`}>
        <div className={LABEL}>Pick your ride</div>
        <div className="grid grid-cols-4 gap-1.5">
          {HILL_RIDES.map((r, i) => (
            <button
              key={r}
              type="button"
              tabIndex={-1}
              aria-pressed={r === ride}
              aria-label={r === ride ? `Drop in on ${RIDE_LABELS[r]}` : RIDE_LABELS[r]}
              onMouseDown={noFocus}
              onClick={() => (r === ride ? onDropIn?.() : choose(i))}
              className={`${tile(r === ride)} pointer-events-auto min-h-12 w-16 touch-manipulation select-none sm:w-20`}
            >
              <span aria-hidden="true" className="text-2xl">
                {RIDE_ICONS[r]}
              </span>
              <span className="text-[0.6rem] leading-tight">{RIDE_LABELS[r]}</span>
            </button>
          ))}
        </div>
        <div className={`${NOTE} flex flex-wrap justify-center gap-x-4 uppercase`}>
          <span>{input === "gamepad" ? `${drop} Drop in · or Start` : input === "touch" ? `${drop} Drop in · or tap it` : `${drop} Drop in`}</span>
          <span>{STEP_KEYS[input]} Ride</span>
        </div>
      </div>
    </div>
  );
}

/**
 * The tutorial's lesson (#201): what to do now, named for your input, which
 * of how many, and Skip. After a run ends partway it says to drop in again;
 * the lesson carries on from there.
 */
function LessonCard({ lesson, input, onSkip }: { lesson: NonNullable<HillHudState["lesson"]>; input: InputKind; onSkip?: () => void }) {
  return (
    <div role="status" aria-label="Tutorial" data-hill-lesson className={`${PANEL} pointer-events-none flex max-w-[min(92vw,28rem)] items-center gap-3 py-1.5 pl-3 pr-1.5`}>
      <div className="min-w-0 flex-1">
        <div className={`${LABEL_TYPE} text-label`}>
          Tutorial · {lesson.step} of {lesson.of}
        </div>
        <div className="text-sm font-black sm:text-base">{lesson.again ? `Drop in again: ${namedFor(input, lesson.text)}` : namedFor(input, lesson.text)}</div>
      </div>
      {onSkip && (
        <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={onSkip} className={`${SECONDARY} pointer-events-auto relative z-10 shrink-0 px-3 py-1 text-xs`}>
          Skip
        </button>
      )}
    </div>
  );
}

/** Speed against wobble speed: green up to it, red past it, the needle where you are. */
function SpeedGauge({ hill }: { hill: HillHudState }) {
  // The bar runs to 1.4x wobble speed; the line at 1 is where the wobbles start.
  const at = Math.min(1, hill.gauge / 1.4) * 100;
  const fast = hill.gauge > 1;
  return (
    <div aria-label="Speed" className="flex w-[min(80vw,22rem)] flex-col items-center gap-1">
      <div className="relative h-4 w-full rounded-full border-4 border-ink bg-white/80">
        <div className="absolute inset-y-0 right-0 w-[28.6%] rounded-r-full bg-danger-soft" />
        <div className={`absolute inset-y-0 left-0 rounded-l-full ${fast ? "bg-danger" : "bg-aqua"}`} style={{ width: `${at}%` }} />
      </div>
      <div className={`text-sm font-black uppercase italic tabular-nums tracking-widest text-white ${outline}`}>{hill.speed} km/h</div>
    </div>
  );
}

/** How strong the speed streaks are (#194): none until 60% of wobble speed, full past it. */
/** The speed as a slim bar along the screen's top edge, for a phone (#198): the wobble zone red at its end. */
function SpeedEdge({ hill }: { hill: HillHudState }) {
  const at = Math.min(1, hill.gauge / 1.4) * 100;
  const fast = hill.gauge > 1;
  return (
    <div aria-label="Speed" data-edge="top" title={`${hill.speed} km/h`} className="absolute inset-x-0 top-0 h-2 bg-ink/40">
      <div className="absolute inset-y-0 right-0 w-[28.6%] bg-danger-soft/80" />
      <div className={`absolute inset-y-0 left-0 ${fast ? "bg-danger" : "bg-aqua"}`} style={{ width: `${at}%` }} />
    </div>
  );
}

/** What's going wrong under you, while the run's on: the wobbles, a slide, gravel. */
function warning(hill: HillHudState): string | null {
  if (hill.end) return null;
  if (hill.sliding) return "Sliding!";
  if (hill.wobble >= 0.2) return "Speed wobbles! Carve!";
  return hill.gravel ? "Gravel! No grip!" : null;
}

export function speedLines(gauge: number): number {
  return Math.round(Math.min(1, Math.max(0, (gauge - 0.6) / 0.6)) * 100) / 100;
}

/** Streaks rushing out from the middle of the screen, stronger the faster you go (#194). */
function SpeedLines({ strength }: { strength: number }) {
  return (
    <div
      data-speed-lines
      aria-hidden
      className="pointer-events-none absolute inset-0 animate-pulse"
      style={{
        opacity: strength,
        background: "repeating-conic-gradient(from 0deg at 50% 45%, transparent 0deg 5deg, rgb(255 255 255 / 0.7) 5.6deg, transparent 6.4deg 11deg)",
        maskImage: "radial-gradient(ellipse at 50% 45%, transparent 38%, black 75%)",
        WebkitMaskImage: "radial-gradient(ellipse at 50% 45%, transparent 38%, black 75%)",
      }}
    />
  );
}

/** `compact`: a phone (#198), where the big counter and gauge would sit over the road. */
export function HillHud({
  hill,
  compact = false,
  input = "keyboard",
  onSkipLesson,
  onLobby,
  ...pick
}: { hill: HillHudState; compact?: boolean; input?: InputKind; onSkipLesson?: () => void; onLobby?: () => void } & PickActions) {
  const lesson = hill.lesson && !hill.end ? <LessonCard lesson={hill.lesson} input={input} onSkip={onSkipLesson} /> : null;
  const end = hill.end;
  const streaks = end ? 0 : speedLines(hill.gauge);
  const warn = warning(hill);
  if (compact)
    return (
      <>
        {streaks > 0 && <SpeedLines strength={streaks} />}
        <SpeedEdge hill={hill} />
        <div className="pointer-events-none absolute inset-x-0 top-[max(0.75rem,env(safe-area-inset-top))] flex flex-col items-center text-white">
          <div className="flex items-baseline gap-3">
            <div aria-label="Distance" className={`text-2xl font-black italic tabular-nums ${outline}`}>
              {hill.distance} m
            </div>
            <div className={`text-xs font-black uppercase italic tabular-nums text-highlight ${outline}`}>Best {hill.best} m</div>
            {hill.closeCalls > 0 && (
              <div aria-label="Close calls" className={`text-xs font-black uppercase italic tabular-nums ${outline}`}>
                +{hill.closeCalls * HILL_CLOSE_BONUS} m
              </div>
            )}
          </div>
          {!hill.picking && <ScoreLine hill={hill} compact />}
          {warn && (
            <div role="status" className={`animate-pulse text-sm font-black uppercase italic text-danger-soft ${outline}`}>
              {warn}
            </div>
          )}
          {lesson && <div className="mt-1">{lesson}</div>}
        </div>
        {hill.picking && !end && <PickCard ride={hill.picking} input={input} {...pick} />}
        {end && <EndCard hill={hill} end={end} onLobby={onLobby} />}
      </>
    );
  return (
    <>
      {streaks > 0 && <SpeedLines strength={streaks} />}
      {/* Below the icon buttons until the screen's wide enough for both. */}
      <div className="pointer-events-none absolute inset-x-0 top-[max(4.5rem,env(safe-area-inset-top))] flex flex-col items-center text-white lg:top-[max(1rem,env(safe-area-inset-top))]">
        <div className={`text-xs font-black uppercase italic tracking-widest text-highlight sm:text-sm ${outline}`}>Down the hill</div>
        <div aria-label="Distance" className={`text-5xl font-black italic tabular-nums sm:text-6xl ${outline}`}>
          {hill.distance} m
        </div>
        <div className={`text-sm font-black uppercase italic tabular-nums ${outline}`}>Best {hill.best} m</div>
        {!hill.picking && <ScoreLine hill={hill} compact={false} />}
        {hill.closeCalls > 0 && (
          <div aria-label="Close calls" className={`text-sm font-black uppercase italic tabular-nums text-highlight ${outline}`}>
            {hill.closeCalls === 1 ? "1 close call" : `${hill.closeCalls} close calls`} +{hill.closeCalls * HILL_CLOSE_BONUS} m
          </div>
        )}
      </div>
      {hill.picking && !end && <PickCard ride={hill.picking} input={input} {...pick} />}
      {end && <EndCard hill={hill} end={end} onLobby={onLobby} />}
      <div className={`pointer-events-none absolute inset-x-0 ${SAFE_BOTTOM} mb-24 flex flex-col items-center gap-2`}>
        {lesson}
        {warn && (
          <div role="status" className={`animate-pulse text-center text-2xl font-black uppercase italic text-danger-soft sm:text-3xl ${outline}`}>
            {warn}
          </div>
        )}
        <SpeedGauge hill={hill} />
      </div>
    </>
  );
}
